import { WatchmanFetch } from '../../../shared/utils/watchmanFetch';

/**
 * Merchant return/exchange policy (PE-2162/PE-2163), stored on the Watchman
 * MerchantStoreConfig alongside VAT and shipping zones and routed through
 * osprey-api's /iam proxy.
 *
 * Writes go through the merchant SELF-SERVICE route `PATCH /merchants/me/store-config`
 * — watchman resolves the merchant from the JWT (`user.merchantId`), so this is only
 * callable by a MERCHANT-scoped session. There is deliberately no admin-by-id write
 * here: watchman exposes `PATCH :merchantId/vat-config` but no return-policy
 * counterpart, so TCS staff get the read-only admin view below.
 *
 * order-service reads the same row over a service token at
 * `GET /merchants/:merchantId/return-policy`.
 */

/** watchman ReturnInventoryRestockTrigger — when the original variant goes back into stock. */
export type ReturnInventoryRestockTrigger = 'ON_RECEIPT' | 'ON_APPROVAL';

export interface ReturnPolicy {
  /** Master switch — everything below is inert while this is false. */
  returnsEnabled: boolean;
  /** Days after delivery a customer may open a request. */
  returnWindowDays: number | null;
  /** Days after approval to ship the item back before an exchange auto-cancels. */
  returnShipmentWindowDays: number | null;
  /** product-service category ids that cannot be returned or exchanged. */
  ineligibleCategoryIds: string[];
  restockingFeeEnabled: boolean;
  /** Percentage of the refunded line, 0-100 (NOT a fraction, and NOT minor units). */
  restockingFeePercentage: number | null;
  restockInventoryTrigger: ReturnInventoryRestockTrigger;
  returnShippingCostAbsorbedByMerchant: boolean;
}

/** The subset of the store-config row this feature reads/writes. */
export interface StoreConfigReturnPolicy extends ReturnPolicy {
  merchantId: string;
  /** Free-text policy shown on the storefront; predates the structured fields. */
  returnPolicy: string | null;
}

export type UpdateReturnPolicyPayload = Partial<ReturnPolicy> & { returnPolicy?: string };

export const returnsPolicyApi = {
  /** Merchant reads their own row (watchman creates an empty one on first access). */
  getMine: (): Promise<StoreConfigReturnPolicy> =>
    WatchmanFetch.get('/merchants/me/store-config'),

  /** Merchant self-service write. Omitted fields are left untouched by watchman. */
  updateMine: (data: UpdateReturnPolicyPayload): Promise<StoreConfigReturnPolicy> =>
    WatchmanFetch.patch('/merchants/me/store-config', data),

  /** TCS admin read-only view of a merchant's policy (MERCHANT.READ). */
  getForMerchant: (merchantId: string): Promise<StoreConfigReturnPolicy> =>
    WatchmanFetch.get(`/merchants/${merchantId}/store-config`),
};
